//This http interceptor service is used to limit the number of active requests at one time
//It creates a queueing system for requests over the limit and processes those as slots
//become available.
//To use this service, inject it into your page constructor and set throttleOn in ngOnInit and turn
//it back off in ngOnDestroy.
import { HttpRequest, HttpHandler, HttpEvent, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { tap, catchError, concatMap } from "rxjs/operators";

// The max http connections we can can use
// For chrome and edge max executing requests is 6, ie 11 is 13
// We could determine browser and set this accordingly, but do we want to code for ie11??
const THROTTLE_LIMIT = 6;

@Injectable()
export class APIThrottleService {
    private activeCount = 0;
    private reqQueue = [];
    private throttleOn : boolean = false;

    public get enabled(): boolean { return this.throttleOn; }
    public set enabled(value: boolean) { 
        this.throttleOn = value;
        if (this.throttleOn === false && (this.activeCount > 0 || this.reqQueue.length > 0)) {
            this.activeCount = 0;
            this.reqQueue = [];
        }
    }

    public intercept(
        req: HttpRequest<any>,
        next: HttpHandler,
    ): Observable<HttpEvent<any>> {

        if (!this.throttleOn) {
            return next.handle(req);
        }

        //Can process now - go ahead
        if (this.activeCount < THROTTLE_LIMIT) {
            // console.log('Processing ' + req.url);
            this.activeCount++;
            return next.handle(req).pipe(
                tap(evt => {
                    if (evt instanceof HttpResponse) {
                        this.processResponse();
                    }
                    return evt;
                }),
                catchError(err => {
                    this.processResponse();
                    return of(err);
                }),
            );
        } 

        //At request limit - set up observable to wait for next opening
        const obs = new Observable(ob => {
            this.reqQueue.push(ob);
        });
        
        return obs.pipe(
            concatMap(_ => {
                return next.handle(req).pipe(
                    tap(evt => {
                        if (evt instanceof HttpResponse) {
                            this.processResponse();
                        }
                        return evt;
                    }),
                    catchError(err => {
                        this.processResponse();
                        return of(err);
                    }),
                )
            })
        );                
    }
    
    private processResponse() {
        if (this.activeCount > 0) {
            this.activeCount--;
        }
        if (this.reqQueue.length > 0) {
            //Get next request to process
            let reqObj = this.reqQueue.shift();
            if (reqObj) {
                const observer = reqObj;
                observer.next('done!');
                observer.complete();
            }
        }
    }
}    
